import React from "react";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { GitBranch } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useApplicationTriggerLinks } from "@/hooks/process-trigger/use-process-trigger";
import {
  managementApplicationPath,
  type TriggerRun,
} from "@/types/process-trigger.types";
import RunStatusBadge from "./run-status-badge";
import TriggerRunActions from "./trigger-run-actions";

interface Props {
  applicantProcessId: string;
  isAdmin?: boolean;
}

const personName = (user: TriggerRun["targetUser"]) =>
  user ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || user.email : "—";

/** Processes this application started, and the one it was started from. */
const ApplicationTriggerPanel: React.FC<Props> = ({ applicantProcessId, isAdmin }) => {
  const { t } = useTranslation();
  const { data } = useApplicationTriggerLinks(applicantProcessId);
  const base = isAdmin ? "admin" : "staff";

  if (!data || (data.started.length === 0 && !data.origin)) return null;
  const { started, origin } = data;

  return (
    <section className="my-6 rounded-lg border border-gray-200 bg-white p-4 space-y-4">
      {origin && (
        <p className="flex items-center gap-2 text-sm text-gray-700">
          <GitBranch className="h-4 w-4 text-darkBlue" />
          {t("processTriggers.panel.startedFrom")}{" "}
          <Link
            className="text-darkBlue underline"
            to={managementApplicationPath(
              base,
              origin.parentApplicantProcess.applicationType,
              origin.parentApplicantProcess.processId,
              origin.parentApplicantProcess.id
            )}
          >
            {origin.parentApplicantProcess.process.name}
          </Link>
        </p>
      )}

      {started.length > 0 && (
        <div>
          <h3 className="flex items-center gap-2 font-medium text-darkBlue mb-2">
            <GitBranch className="h-4 w-4" /> {t("processTriggers.panel.title")}
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-3 font-normal">{t("processTriggers.runs.process")}</th>
                  <th className="py-2 pr-3 font-normal">{t("processTriggers.runs.recipient")}</th>
                  <th className="py-2 pr-3 font-normal">{t("processTriggers.runs.status")}</th>
                  <th className="py-2 pr-3 font-normal">{t("processTriggers.runs.date")}</th>
                  {isAdmin && <th className="py-2 font-normal" />}
                </tr>
              </thead>
              <tbody>
                {started.map((run) => (
                  <tr key={run.id} className="border-b last:border-0 align-top">
                    <td className="py-2 pr-3">
                      {run.childApplicantProcess ? (
                        <Link
                          className="text-darkBlue underline"
                          to={managementApplicationPath(
                            base,
                            run.childApplicantProcess.applicationType,
                            run.targetProcess.id,
                            run.childApplicantProcess.id
                          )}
                        >
                          {run.targetProcess.name}
                        </Link>
                      ) : (
                        run.targetProcess.name
                      )}
                    </td>
                    <td className="py-2 pr-3">{personName(run.targetUser)}</td>
                    <td className="py-2 pr-3 space-y-1">
                      <RunStatusBadge status={run.status} />
                      {run.statusReason && (
                        <p className="text-xs text-gray-500">{run.statusReason}</p>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-gray-600 whitespace-nowrap">
                      {dayjs(run.createdAt).format("MMM D, YYYY")}
                      {run.dueAt && run.status === "AWAITING_USER" && (
                        <p className="text-xs text-gray-500">
                          {t("processTriggers.todo.dueOn", {
                            date: dayjs(run.dueAt).format("MMM D, YYYY"),
                          })}
                        </p>
                      )}
                    </td>
                    {isAdmin && (
                      <td className="py-2">
                        <TriggerRunActions run={run} />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
};

export default ApplicationTriggerPanel;
